import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { genereazaPlan, getPlanuri, addTracker } from '../services/api';

interface Exercitiu {
  nume: string;
  descriere: string;
  repetari: string;
  durata_minute: number;
  contraindicatii: string;
  dificultate: string;
}

interface Zi {
  zi: string;
  exercitii: Exercitiu[];
}

interface Saptamana {
  saptamana: number;
  focus: string;
  zile: Zi[];
}

interface PlanJSON {
  titlu: string;
  saptamani: Saptamana[];
  recomandari_generale: string;
}

interface ModalState {
  open: boolean;
  exNume: string;
  exKey: string;
  nivelDurere: number;
  dificultate: string;
  loading: boolean;
}

const MODAL_INIT: ModalState = {
  open: false, exNume: '', exKey: '',
  nivelDurere: 5, dificultate: '', loading: false,
};

const parsePlan = (plan: any): PlanJSON | null => {
  if (plan && typeof plan === 'object' && Array.isArray(plan.saptamani)) return plan as PlanJSON;
  if (typeof plan === 'string') {
    try {
      const p = JSON.parse(plan);
      if (p && Array.isArray(p.saptamani)) return p as PlanJSON;
    } catch {}
  }
  return null;
};

const badgeDificultate = (dif: string): { bg: string; color: string } => {
  const d = (dif || '').toLowerCase();
  if (d.includes('ușor') || d.includes('usor') || d.includes('facil'))
    return { bg: '#1a3a2a', color: '#60d090' };
  if (d.includes('mediu') || d.includes('moderat'))
    return { bg: '#3a3a1a', color: '#d0c060' };
  return { bg: '#3a1a1a', color: '#d06060' };
};

const OPTIUNI_DIFICULTATE = ['Ușor', 'Ok', 'Greu', 'Am oprit'];

const PlanStructurat: React.FC<{ plan: PlanJSON; collapsible?: boolean }> = ({ plan, collapsible = false }) => {
  const [completate, setCompletate] = useState<Set<string>>(new Set());
  const [expandate, setExpandate] = useState<Set<number>>(new Set(collapsible ? [] : [1, 2, 3, 4]));
  const [modal, setModal] = useState<ModalState>(MODAL_INIT);

  const toggleSaptamana = (nr: number) => {
    if (!collapsible) return;
    setExpandate(prev => {
      const next = new Set(prev);
      next.has(nr) ? next.delete(nr) : next.add(nr);
      return next;
    });
  };

  const deschideModal = (exKey: string, exNume: string) => {
    setModal({ ...MODAL_INIT, open: true, exKey, exNume });
  };

  const inchideModal = () => setModal(MODAL_INIT);

  const handleSalveaza = async () => {
    if (!modal.dificultate) return;
    setModal(prev => ({ ...prev, loading: true }));
    try {
      await addTracker(
        modal.nivelDurere,
        'Generalizata',
        `Plan completat: ${modal.exNume} - Dificultate: ${modal.dificultate}`,
        true
      );
      setCompletate(prev => new Set(prev).add(modal.exKey));
      toast.success('Exercițiu salvat în tracker!');
      inchideModal();
    } catch {
      toast.error('Eroare la salvare. Încearcă din nou!');
      setModal(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <div>
      <h3 style={{ color: '#60d090', marginBottom: '1.5rem', fontSize: '1.1rem' }}>{plan.titlu}</h3>

      {plan.saptamani?.map(spt => {
        const esteExpandata = expandate.has(spt.saptamana);
        return (
          <div key={spt.saptamana} style={{ marginBottom: '0.75rem' }}>
            <div
              onClick={() => toggleSaptamana(spt.saptamana)}
              style={{
                background: '#12121f', borderRadius: esteExpandata ? '10px 10px 0 0' : '10px',
                padding: '0.75rem 1rem', cursor: collapsible ? 'pointer' : 'default',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                border: '1px solid #2a2a4a', borderBottom: esteExpandata ? 'none' : '1px solid #2a2a4a'
              }}
            >
              <span style={{ color: '#c0c0e0', fontWeight: 600 }}>
                Săptămâna {spt.saptamana} — <span style={{ color: '#8080b0', fontWeight: 400 }}>{spt.focus}</span>
              </span>
              {collapsible && (
                <span style={{ color: '#4a4a8a', fontSize: '0.8rem' }}>{esteExpandata ? '▲' : '▼'}</span>
              )}
            </div>

            {esteExpandata && (
              <div style={{ border: '1px solid #2a2a4a', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '1rem', background: '#0d0d20' }}>
                {spt.zile?.map(zi => (
                  <div key={zi.zi} style={{ marginBottom: '1.25rem' }}>
                    <p style={{ color: '#6060a0', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                      {zi.zi}
                    </p>
                    {zi.exercitii?.map((ex, idx) => {
                      const key = `${spt.saptamana}-${zi.zi}-${idx}`;
                      const facut = completate.has(key);
                      const badge = badgeDificultate(ex.dificultate);
                      return (
                        <div key={idx} style={{
                          background: facut ? '#0a2a1a' : '#12121f',
                          border: `1px solid ${facut ? '#2a6a4a' : '#2a2a4a'}`,
                          borderRadius: '10px', padding: '1rem', marginBottom: '0.6rem',
                          transition: 'background 0.2s, border-color 0.2s'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                            <strong style={{ color: '#e0e0f0', fontSize: '0.95rem' }}>{ex.nume}</strong>
                            <span style={{
                              background: badge.bg, color: badge.color,
                              fontSize: '0.72rem', padding: '0.2rem 0.55rem',
                              borderRadius: '99px', flexShrink: 0, marginLeft: '0.75rem', fontWeight: 600
                            }}>
                              {ex.dificultate}
                            </span>
                          </div>
                          <p style={{ color: '#a0a0c0', fontSize: '0.88rem', lineHeight: 1.55, margin: '0 0 0.5rem' }}>
                            {ex.descriere}
                          </p>
                          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: ex.contraindicatii ? '0.5rem' : '0.75rem', flexWrap: 'wrap' }}>
                            <span style={{ color: '#7070b0', fontSize: '0.82rem' }}>Repetări: {ex.repetari}</span>
                            <span style={{ color: '#7070b0', fontSize: '0.82rem' }}>Durata: {ex.durata_minute} min</span>
                          </div>
                          {ex.contraindicatii && (
                            <p style={{
                              color: '#a06060', fontSize: '0.8rem',
                              margin: '0 0 0.75rem', borderLeft: '2px solid #6a2a2a',
                              paddingLeft: '0.6rem', lineHeight: 1.4
                            }}>
                              {ex.contraindicatii}
                            </p>
                          )}
                          <button
                            onClick={() => !facut && deschideModal(key, ex.nume)}
                            style={{
                              padding: '0.35rem 0.9rem', borderRadius: '8px', border: 'none',
                              fontSize: '0.82rem', fontWeight: 600,
                              background: facut ? '#2a6a4a' : '#2a2a4a',
                              color: facut ? '#60d090' : '#8080a0',
                              cursor: facut ? 'default' : 'pointer',
                              transition: 'all 0.2s'
                            }}>
                            {facut ? '✓ Făcut' : 'Am făcut!'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {plan.recomandari_generale && (
        <div style={{ background: '#12121f', borderRadius: '10px', padding: '1rem', border: '1px solid #2a2a4a', marginTop: '0.5rem' }}>
          <p style={{ color: '#8080a0', fontSize: '0.88rem', margin: 0, lineHeight: 1.6 }}>
            {plan.recomandari_generale}
          </p>
        </div>
      )}

      {/* Modal feedback */}
      {modal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a30', borderRadius: '16px', padding: '2rem',
            width: '100%', maxWidth: '420px', border: '1px solid #2a2a4a'
          }}>
            <h3 style={{ color: '#fff', marginBottom: '0.25rem', fontSize: '1.1rem' }}>Cum te-ai simțit?</h3>
            <p style={{ color: '#6060a0', fontSize: '0.85rem', marginBottom: '1.5rem' }}>{modal.exNume}</p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#a0a0c0', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                Nivel durere după exercițiu: <strong style={{ color: '#fff' }}>{modal.nivelDurere}/10</strong>
              </label>
              <input
                type="range" min={1} max={10} value={modal.nivelDurere}
                onChange={e => setModal(prev => ({ ...prev, nivelDurere: parseInt(e.target.value) }))}
                style={{ width: '100%', accentColor: '#4a4ad8' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.2rem' }}>
                <small style={{ color: '#6060a0', fontSize: '0.72rem' }}>Fără durere</small>
                <small style={{ color: '#6060a0', fontSize: '0.72rem' }}>Durere maximă</small>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#a0a0c0', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                Cât de greu ți s-a părut?
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {OPTIUNI_DIFICULTATE.map(opt => (
                  <button
                    key={opt}
                    onClick={() => setModal(prev => ({ ...prev, dificultate: opt }))}
                    style={{
                      padding: '0.45rem 1rem', borderRadius: '8px',
                      border: `1.5px solid ${modal.dificultate === opt ? '#4a4ad8' : '#2a2a4a'}`,
                      background: modal.dificultate === opt ? '#2a2a6a' : 'transparent',
                      color: modal.dificultate === opt ? '#c0c0f0' : '#8080a0',
                      cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                      transition: 'all 0.15s'
                    }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={inchideModal} style={{
                flex: 1, padding: '0.7rem', borderRadius: '10px',
                border: '1px solid #2a2a4a', background: 'transparent',
                color: '#8080a0', cursor: 'pointer', fontSize: '0.9rem'
              }}>
                Închide
              </button>
              <button
                onClick={handleSalveaza}
                disabled={!modal.dificultate || modal.loading}
                style={{
                  flex: 2, padding: '0.7rem', borderRadius: '10px', border: 'none',
                  background: modal.dificultate && !modal.loading ? '#4a4ad8' : '#2a2a4a',
                  color: modal.dificultate && !modal.loading ? '#fff' : '#6060a0',
                  cursor: modal.dificultate && !modal.loading ? 'pointer' : 'not-allowed',
                  fontWeight: 600, fontSize: '0.9rem', transition: 'all 0.15s'
                }}>
                {modal.loading ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Plan: React.FC = () => {
  const [planuri, setPlanuri] = useState<any[]>([]);
  const [planGenerat, setPlanGenerat] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    problema: 'Sciatică acută (sub 6 săptămâni)',
    nivel_durere: 5,
    varsta: '18-30 ani',
    activitate: 'Sedentar (birou, condus mult)',
    timp: '20-30 minute',
    echipament: ['Fără echipament'],
    alte_afectiuni: '',
    kinetoterapie: 'Nu',
    somn_afectat: false,
    obiectiv: 'Reducerea durerii acute'
  });

  useEffect(() => {
    getPlanuri().then(r => setPlanuri(r.data)).catch(() => {});
  }, []);

  const update = (field: string, value: any) => setForm(prev => ({ ...prev, [field]: value }));

  const handleGenereaza = async () => {
    setLoading(true);
    setPlanGenerat(null);
    try {
      const r = await genereazaPlan(form);
      setPlanGenerat(r.data);
      getPlanuri().then(res => setPlanuri(res.data)).catch(() => {});
    } catch {
      alert('Eroare la generare! Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: '0.75rem', borderRadius: '10px',
    border: '1.5px solid #2a2a4a', background: '#1a1a30',
    color: '#e0e0f0', fontSize: '0.9rem', width: '100%',
    boxSizing: 'border-box' as const
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '820px', margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}>
      <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>Plan de Recuperare Personalizat</h2>
      <p style={{ color: '#8080a0', marginBottom: '2rem' }}>Generează un plan adaptat problemei tale.</p>

      <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a', marginBottom: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Problemă</label>
            <select value={form.problema} onChange={e => update('problema', e.target.value)} style={inputStyle}>
              {['Sciatică acută (sub 6 săptămâni)', 'Sciatică cronică (peste 6 săptămâni)', 'Hernie de disc lombară', 'Durere lombară nespecifică', 'Sindrom piriform', 'Durere cervicală'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Vârstă</label>
            <select value={form.varsta} onChange={e => update('varsta', e.target.value)} style={inputStyle}>
              {['18-30 ani', '31-45 ani', '46-60 ani', 'Peste 60 ani'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>
              Nivel durere: <strong style={{ color: '#fff' }}>{form.nivel_durere}/10</strong>
            </label>
            <input type="range" min={1} max={10} value={form.nivel_durere}
              onChange={e => update('nivel_durere', parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#4a4ad8' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
              <div>
                <span style={{ color: '#6060a0', fontSize: '0.75rem', display: 'block' }}>1</span>
                <small style={{ color: '#6060a0', fontSize: '0.72rem' }}>Fără durere</small>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#6060a0', fontSize: '0.75rem', display: 'block' }}>10</span>
                <small style={{ color: '#6060a0', fontSize: '0.72rem' }}>Nu mă pot mișca/nu pot merge</small>
              </div>
            </div>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Stil de viață</label>
            <select value={form.activitate} onChange={e => update('activitate', e.target.value)} style={inputStyle}>
              {['Sedentar (birou, condus mult)', 'Moderat activ', 'Activ fizic'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Timp disponibil zilnic</label>
            <select value={form.timp} onChange={e => update('timp', e.target.value)} style={inputStyle}>
              {['10-15 minute', '20-30 minute', '45-60 minute', 'Peste 1 oră'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Kinetoterapie anterior</label>
            <select value={form.kinetoterapie} onChange={e => update('kinetoterapie', e.target.value)} style={inputStyle}>
              {['Nu', 'Da, a ajutat', 'Da, dar nu a ajutat'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Obiectiv</label>
            <select value={form.obiectiv} onChange={e => update('obiectiv', e.target.value)} style={inputStyle}>
              {['Reducerea durerii acute', 'Recuperare și revenire normală', 'Prevenirea recidivelor', 'Îmbunătățirea mobilității'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.4rem' }}>Alte afecțiuni</label>
            <input placeholder="Ex: diabet, operat..." value={form.alte_afectiuni}
              onChange={e => update('alte_afectiuni', e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label style={{ color: '#a0a0c0', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>Echipament disponibil</label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {['Fara echipament', 'Saltea yoga', 'Baston/Stick', 'Banda elastica', 'Gantera', 'Scaun', 'Perete'].map(opt => {
              const sel = form.echipament.includes(opt);
              return (
                <button key={opt} type="button"
                  onClick={() => {
                    const next = sel
                      ? form.echipament.filter((e: string) => e !== opt)
                      : [...form.echipament, opt];
                    if (next.length > 0) update('echipament', next);
                  }}
                  style={{
                    padding: '0.35rem 0.75rem', borderRadius: '8px', border: 'none',
                    background: sel ? '#2a2a6a' : '#2a2a4a',
                    color: sel ? '#c0c0f0' : '#8080a0',
                    cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.15s'
                  }}>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.somn_afectat}
              onChange={e => update('somn_afectat', e.target.checked)}
              style={{ accentColor: '#4a4ad8', width: '16px', height: '16px' }} />
            <span style={{ color: '#a0a0c0', fontSize: '0.9rem' }}>Durerea îmi afectează somnul</span>
          </label>
        </div>

        <button onClick={handleGenereaza} disabled={loading} style={{
          width: '100%', marginTop: '1.5rem', padding: '0.75rem', borderRadius: '10px',
          border: 'none', background: '#4a4ad8', color: '#fff', fontWeight: 600,
          fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1
        }}>
          {loading ? 'Se generează planul...' : 'Generează Planul'}
        </button>
      </div>

      {planGenerat && (() => {
        const planParsed = parsePlan(planGenerat);
        return (
          <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a6a4a', marginBottom: '2rem' }}>
            {planParsed ? (
              <PlanStructurat plan={planParsed} collapsible={false} />
            ) : (
              <div style={{ color: '#e0e0f0', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {typeof planGenerat === 'string' ? planGenerat : JSON.stringify(planGenerat, null, 2)}
              </div>
            )}
            <p style={{ color: '#6060a0', fontSize: '0.8rem', marginTop: '1.25rem', borderTop: '1px solid #2a2a4a', paddingTop: '0.75rem' }}>
              Scop educativ. Consultă un kinetoterapeut pentru supervizare.
            </p>
          </div>
        );
      })()}

      {planuri.length > 0 && (
        <div>
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Planurile tale anterioare</h3>
          {planuri.map((p, i) => {
            const planParsed = parsePlan(p.plan);
            return (
              <details key={i} style={{
                background: '#1a1a30', borderRadius: '10px',
                border: '1px solid #2a2a4a', marginBottom: '0.75rem'
              }}>
                <summary style={{
                  color: '#c0c0e0', fontWeight: 600, cursor: 'pointer',
                  padding: '1rem', listStyle: 'none'
                }}>
                  Plan din {p.created_at} — {p.problema}
                  <span style={{ color: '#5a5a8a', fontWeight: 400, fontSize: '0.85rem' }}> (durere {p.nivel_durere}/10)</span>
                </summary>
                <div style={{ padding: '0 1rem 1rem' }}>
                  {planParsed ? (
                    <PlanStructurat plan={planParsed} collapsible={true} />
                  ) : (
                    <div style={{ color: '#a0a0c0', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                      {typeof p.plan === 'string' ? p.plan : JSON.stringify(p.plan, null, 2)}
                    </div>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Plan;
